import { useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_SONG_LENGTH,
  LYRICS_MAX_CHARS,
  type SongLength,
  type StyleChips,
  type WorkResponse,
} from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { loadDraftBackup, useAutosave } from "../lib/autosave";
import { retryOnAutosaveConflict } from "../lib/autosaveConflict";
import { navigate } from "../lib/navigation";
import { pathForStep, updateSession } from "../lib/session";
import { createStyleCompileGate } from "../lib/styleCompileGate";
import { INTRO_MAX_LENGTH, appendSuggestionToIntro } from "../lib/styleIntro";

interface StyleResponse {
  sentence: string;
  tags: string[];
}

const chipOptions = {
  genre: ["K-POP", "발라드", "힙합", "록", "어쿠스틱", "국악풍", "동요·합창"],
  voice: ["남성", "여성", "어린이", "합창", "연주곡"],
  tempo: ["잔잔하게", "보통", "신나게", "터지게"],
} as const;

/** 노래 길이 버튼(2026-10-18 jery). 기본 1분 — 발표·영상 합성 시간을 지키려고 긴 노래는 고른 학생만 만든다. */
const songLengthOptions: ReadonlyArray<{ value: SongLength; label: string }> = [
  { value: "1", label: "1분" },
  { value: "2", label: "2분" },
  { value: "3", label: "3분" },
  { value: "auto", label: "AI가 정하기" },
];

export function StylePage({ workId }: { workId: string }) {
  const query = useWork(workId);
  if (query.loading) return <LoadingPage />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "스타일을 불러오지 못했습니다."} />;
  }
  return <StyleEditor workId={workId} initial={query.data} />;
}

function StyleEditor({ workId, initial }: { workId: string; initial: WorkResponse }) {
  const backup = loadDraftBackup(workId);
  const initialChips = { ...initial.work.chips, ...(backup?.chips ?? {}) };
  const [intro, setIntro] = useState(backup?.intro ?? initial.work.intro ?? "");
  const [chips, setChips] = useState<Partial<StyleChips>>(initialChips);
  const backupChanged = Boolean(
    backup && (
      backup.intro !== undefined && backup.intro !== (initial.work.intro ?? "") ||
      backup.chips !== undefined && JSON.stringify(backup.chips) !== JSON.stringify(initial.work.chips)
    ),
  );
  const [sentence, setSentence] = useState<string | null>(
    backupChanged ? null : initial.work.style_sentence,
  );
  const [tags, setTags] = useState<string[]>([]);
  const [compiling, setCompiling] = useState(false);
  const [creating, setCreating] = useState(false);
  const [songLength, setSongLength] = useState<SongLength>(DEFAULT_SONG_LENGTH);
  const isHumming = initial.work.mode === "humming";
  const lyricsLength = initial.work.lyrics?.trim().length ?? 0;
  const [error, setError] = useState<string | null>(null);
  const payload = useMemo(() => ({
    step: "style" as const,
    mode: initial.work.mode === "humming" ? "humming" as const : "lyrics" as const,
    intro,
    chips,
  }), [chips, intro]);
  const autosave = useAutosave(workId, payload);
  // 🔑 2026-10-06 jery: 소개·칩이 비어 있어도 바로 만든다. 빈 칩은 서버가 곡마다 다르게 고른다.
  //    「완성될 스타일 문장」(AI 정리)은 소개가 있을 때만 만든다 — 없으면 칩만으로 간다.
  const complete = Boolean(intro.trim());
  const dirty = intro !== (initial.work.intro ?? "") ||
    JSON.stringify(chips) !== JSON.stringify(initial.work.chips);

  // 문지기가 부를 때 «지금» 입력을 읽게 한다(문지기는 처음 한 번만 만든다).
  const latestInput = useRef({ intro, chips });
  latestInput.current = { intro, chips };
  const [gate] = useState(() => createStyleCompileGate<StyleResponse>((signal, isCurrent) =>
    // 409 AUTOSAVE_CONFLICT = 다른 저장이 먼저 끝났다 — 이 정리가 아직 최신이면 같은 입력으로 다시 보낸다
    retryOnAutosaveConflict(() => apiRequest<StyleResponse>("/api/ai/style-compile", {
      method: "POST",
      signal,
      body: JSON.stringify({ intro: latestInput.current.intro.trim(), chips: latestInput.current.chips }),
    }), isCurrent)));

  /**
   * 스타일 문장·제안 칩을 만든다.
   *
   * 🔑 2026-08-14 jery QA: 디바운스만 방아쇠라 학생이 «칩이 왜 안 나오지» 에 갇혔다 → blur 에 즉시 돌렸다.
   * 🩸 2026-10-15 스타일 정리 429 재발 5회째: 그때 350ms 로 줄인 디바운스가 글자 하나마다 AI 를 불러
   *    한 기기가 1분에 26회를 보냈다(서버 제한 = 참가자당 분당 20회). 그래서 **타이핑 중 호출을 없앴다.**
   *    이제 방아쇠는 둘뿐이다: ① 소개 칸을 벗어날 때(blur) ② 「노래 만들기」를 눌렀는데 문장이 없을 때.
   *    둘이 겹쳐도 같은 입력이면 1번만 부른다(styleCompileGate).
   */
  const compileStyle = async (): Promise<string | null> => {
    if (!complete) return null;
    if (sentence) return sentence;
    const version = gate.version();
    setCompiling(true);
    setError(null);
    try {
      const result = await gate.request();
      if (!result || version !== gate.version()) return null;
      setSentence(result.sentence);
      setTags(result.tags);
      return result.sentence;
    } catch (caught: unknown) {
      // 입력이 바뀌어 취소된 요청(AbortError)은 version 이 달라져 여기서 걸러진다.
      if (version === gate.version()) {
        setError(caught instanceof ApiError ? caught.message : "스타일을 정리하지 못했습니다.");
      }
      return null;
    } finally {
      if (version === gate.version()) setCompiling(false);
    }
  };

  const invalidateCompile = (): void => {
    gate.invalidate();
    setSentence(null);
    setTags([]);
    setCompiling(false);
  };

  /**
   * 칩·제안 칩은 «한 번 누르는» 입력이라 누른 직후가 곧 입력이 끝난 때다(Codex 검수 2026-10-15).
   * 안 그러면 칩을 누른 뒤 문장·제안이 지워진 채 학생이 소개 칸을 다시 나갈 때까지 비어 있다.
   * 새 입력이 화면에 반영된 다음 렌더에서 1번 부른다 — 타이머가 아니라 렌더 뒤 1회다.
   * 소개 칸에 커서가 있으면(그 칸에서 계속 쓰는 중) 부르지 않고 blur 에 맡긴다.
   */
  const introRef = useRef<HTMLTextAreaElement>(null);
  const compileAfterPick = useRef(false);
  // 소개를 쓰다가 칩을 누르면 blur 가 «옛 칩» 으로 먼저 부르고, 칩 선택이 그걸 취소한 뒤 다시 부른다(2번 · Codex 재검수).
  // 칩을 누르기 시작한 순간(pointerdown — blur 보다 먼저 온다)을 적어 두고, 그 blur 는 칩 선택에 맡긴다.
  const chipPressedAt = useRef(0);
  const markChipPress = (): void => { chipPressedAt.current = Date.now(); };
  useEffect(() => {
    if (!compileAfterPick.current) return;
    compileAfterPick.current = false;
    if (document.activeElement === introRef.current) return;
    void compileStyle();
  }, [chips, intro]);

  const chooseChip = (group: keyof StyleChips, value: string): void => {
    // 이미 고른 칩을 다시 누르면 입력이 그대로다 — 문장을 버리고 다시 부를 이유가 없다(Codex 검수).
    if (chips[group] === value) { void compileStyle(); return; }
    invalidateCompile();
    compileAfterPick.current = true;
    setChips((current) => ({ ...current, [group]: value }));
  };

  /**
   * 제안 칩을 누르면 **소개 문장에 그 말을 덧붙인다**(2026-08-07 QA).
   *
   * 이전에는 `<span>` 이라 "+ 태그" 로 눌러보라고 생겨 놓고 아무 일도 일어나지 않았다.
   * 스타일 문장은 서버가 `intro` + `chips` 로만 만들므로, 소개에 덧붙이는 것만으로
   * 제안이 실제로 노래에 반영된다 — 서버 계약은 그대로다.
   */
  const appendSuggestion = (tag: string): void => {
    const next = appendSuggestionToIntro(intro, tag);
    if (next === null) { void compileStyle(); return; }
    invalidateCompile();
    compileAfterPick.current = true;
    setIntro(next);
  };

  const goBack = async (): Promise<void> => {
    try {
      const previousStep = initial.work.mode === "humming" ? "humming" as const : "lyrics" as const;
      await autosave.flush({ ...payload, step: previousStep });
      navigate(pathForStep(workId, previousStep));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "저장하지 못했습니다.");
    }
  };

  /**
   * 🔑 2026-08-14 jery QA — 「가드가 많으면 버그만 많아진다」.
   *    예전 버튼은 소개·칩 3종·컴파일 완료가 모두 맞아야 눌렸다. 그래서 학생 화면에는
   *    **왜 안 눌리는지 말해 주지 않는 죽은 버튼**만 남았다. 이제 버튼은 항상 눌리고,
   *    부족한 게 있으면 «무엇이» 부족한지 말하고 그 자리로 데려간다. 문장이 아직 안 만들어졌으면
   *    여기서 만들고 이어서 진행한다 — 학생이 디바운스를 기다릴 이유가 없다.
   *
   *    ⚠️ 남긴 가드는 둘뿐이다: 남은 생성 0(비용·한도라 UX 로 풀 수 없다)과 중복 클릭.
   *       소개·칩은 서버도 필수로 막는다(work.ts POST /api/works/:id/music 422) —
   *       여기서 안내하지 않으면 학생은 서버 오류 문구를 보게 된다.
   */
  const createMusic = async (): Promise<void> => {
    if (creating || initial.remaining_slots <= 0) return;
    setCreating(true);
    setError(null);
    try {
      // 소개가 있으면 스타일 문장을 먼저 정리한다. 정리에 실패해도 막지 않는다 —
      // 칩(빈 칸은 서버가 고른다)만으로도 곡은 만들어진다.
      if (intro.trim() && !sentence) await compileStyle();
      setError(null);
      await autosave.flush(payload);
      const result = await apiRequest<{ job_id: string }>(
        `/api/works/${encodeURIComponent(workId)}/music`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify(isHumming ? {} : { song_length: songLength }),
        },
      );
      updateSession({ activeJobId: result.job_id, cancelRequested: false });
      navigate(pathForStep(workId, "result"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "노래 만들기를 시작하지 못했습니다.");
    } finally {
      setCreating(false);
    }
  };

  // 결과 화면으로 가기 전에 단계를 result 로 저장한다 — 안 그러면 수업 종료 안내가 곡 고르기 화면을 덮는다.
  const openResult = async (): Promise<void> => {
    try {
      await autosave.flush({ ...payload, step: "result" });
      navigate(pathForStep(workId, "result"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "저장하지 못했습니다.");
    }
  };

  return (
    <StudioShell
      currentStep={3}
      remainingSlots={initial.remaining_slots}
      dirty={dirty}
      autosaveStatus={autosave.status}
      notice={autosave.status === "error" ? "네트워크가 끊겼어요. 소개와 선택은 이 기기에 보관하고 있어요." : undefined}
    >
      <div className="editor-split style-layout">
        <section className="editor-main-panel style-story-panel">
          <h1 tabIndex={-1}>이 노래를 한 문장으로 소개하면?</h1>
          <p className="page-lead left">이 문장이 노래의 개성을 만들어요 — 구체적일수록 남들과 다른 곡이 나와요</p>
          <label className="sr-only" htmlFor="song-intro">노래 소개</label>
          <textarea
            id="song-intro"
            ref={introRef}
            className="intro-textarea"
            value={intro}
            onChange={(event) => {
              invalidateCompile();
              setIntro(event.target.value);
            }}
            onBlur={() => {
              // 칩을 누르느라 생긴 blur 면 건너뛴다 — 칩 선택(또는 같은 칩이면 그 자리)이 1번 부른다.
              if (Date.now() - chipPressedAt.current < 1_000) return;
              void compileStyle();
            }}
            maxLength={INTRO_MAX_LENGTH}
            placeholder="예: 지고 있다가 마지막에 뒤집는 노래"
          />
          <CharCounter value={intro} max={INTRO_MAX_LENGTH} />
          <div className="example-tags" aria-label="소개 예시">
            <span>이런 식으로:</span>
            <span>비 오는 날 버스 안에서 듣는 노래</span>
            <span>급식실로 뛰어갈 때 나오는 노래</span>
          </div>
          {tags.length > 0 ? (
            <div className="suggestion-area">
              <strong>내 문장을 읽고 제안했어요</strong>
              <p className="suggestion-hint">누르면 소개 문장에 더해져요</p>
              <div className="chip-row">
                {tags.map((tag) => (
                  <button
                    type="button"
                    className="suggestion-chip"
                    onPointerDown={markChipPress}
                    key={tag}
                    onClick={() => appendSuggestion(tag)}
                    aria-label={`소개에 '${tag}' 넣기`}
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="style-preview" aria-live="polite">
            <strong>완성될 스타일 문장</strong>
            <p>{compiling ? <InlineSpinner label="내 소개와 선택을 정리하는 중..." /> : sentence ? `“${sentence}”` : complete ? "소개를 다 쓰고 칸 밖을 누르면 정리해요. 「노래 만들기」를 눌러도 정리해요." : "소개를 적으면 자동으로 만들어요. 비워 둬도 노래는 만들 수 있어요."}</p>
            <small>내가 고른 칩 + 소개 문장을 합쳐 4~7개 서술로 정리한 문장이 노래를 만들어요</small>
          </div>
          {error ? <InlineError>{error}</InlineError> : null}
        </section>
        <aside className="style-chip-panel">
          {(Object.keys(chipOptions) as Array<keyof StyleChips>).map((group) => (
            <fieldset className="chip-fieldset" key={group}>
              <legend>{group === "genre" ? "장르" : group === "voice" ? "목소리" : "빠르기·에너지"}</legend>
              <div className="chip-row">
                {chipOptions[group].map((option) => (
                  <button
                    type="button"
                    className={`choice-chip${chips[group] === option ? " selected" : ""}`}
                    aria-pressed={chips[group] === option}
                    onPointerDown={markChipPress}
                    key={option}
                    onClick={() => chooseChip(group, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
          {/* 허밍은 녹음 길이를 따라가므로 고르지 않는다. */}
          {!isHumming ? (
            <fieldset className="chip-fieldset">
              <legend>노래 길이</legend>
              <div className="chip-row">
                {songLengthOptions.map((option) => (
                  <button
                    type="button"
                    className={`choice-chip${songLength === option.value ? " selected" : ""}`}
                    aria-pressed={songLength === option.value}
                    key={option.value}
                    onClick={() => {
                      setSongLength(option.value);
                      // 「1분에는 가사가 너무 길어요」 오류는 길이를 바꾸면 더는 맞지 않는다 — 남겨 두면 3분을 골라도 빨간 글씨가 그대로다(2026-10-18 QA).
                      setError(null);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {songLength === "1" && lyricsLength > LYRICS_MAX_CHARS ? (
                <p className="field-guidance">가사가 {lyricsLength}자라서 1분 노래에는 다 들어가지 않아요. 2분이나 3분 노래로 만들면 가사가 다 들어가요.</p>
              ) : songLength === "auto" ? (
                <p className="field-guidance">AI가 가사에 맞춰 길이를 정해요. 3분을 넘지는 않아요.</p>
              ) : null}
            </fieldset>
          ) : null}
          <div className="style-actions">
            <button className="button-secondary" type="button" onClick={() => void goBack()}>이전</button>
            <button
              className="button-primary"
              type="button"
              onClick={() => void createMusic()}
              disabled={creating || initial.remaining_slots <= 0}
            >
              {creating ? <InlineSpinner label="노래 준비 중..." /> : "노래 만들기"}
            </button>
          </div>
          <p className="field-guidance">안 고른 칸은 AI가 노래마다 다르게 골라요.</p>
          {initial.remaining_slots <= 0 ? <p className="limit-guidance">남은 생성이 없어요. 선생님께 요청해 주세요.</p> : null}
          {/* 🚨 횟수를 다 쓴 학생이 만든 곡으로 갈 길(Codex 검수 2026-10-13 P0-3). */}
          {initial.remaining_slots <= 0 && initial.tracks.length > 0 ? (
            <button className="button-primary" type="button" onClick={() => void openResult()}>
              내가 만든 노래 보러 가기
            </button>
          ) : null}
          <p className="style-footnote">먼저 1곡을 만들고, 아쉬우면 다른 해석으로 하나 더 만들어 비교할 수 있어요</p>
        </aside>
      </div>
    </StudioShell>
  );
}
